import { useCallback, useRef } from 'react'
import { GraphQLTaggedNode, useMutation } from 'react-relay'
import { RRNLRequestError } from 'react-relay-network-modern'
import { MutationParameters } from 'relay-runtime'
import { toast } from 'sonner'

import { captureWithContext } from './sentry/tracking'

interface PortalMutationConfig<TResponse = unknown> {
  /** Custom success message for the toast */
  successMessage?: string
  /** Custom error message for the toast. If not provided, will extract from error */
  errorMessage?: string
  /** Whether to show error toast. Default: true */
  showErrorToast?: boolean
  /** Additional Sentry context for error tracking */
  sentryContext?: Record<string, unknown>
  /** Optional mutation name for tracking */
  mutationName?: string
  /** Enable automatic retry for network errors. Default: true */
  enableRetry?: boolean
  /** Custom onCompleted handler */
  onCompleted?: (response: TResponse) => void
  /** Custom onError handler */
  onError?: (error: unknown) => void
}

/**
 * Check if an error is retryable (network/timeout errors)
 */
function isRetryableError(error: unknown): boolean {
  if (error instanceof RRNLRequestError) {
    // Network errors are typically retryable
    const status = error.res?.status
    if (status && status >= 500) {
      return true // Server errors are retryable
    }
    if (status === 429) {
      return true // Rate limiting is retryable
    }
    if (status === 408) {
      return true // Request timeout is retryable
    }
    // Check for network-related error messages
    const message = error.message?.toLowerCase() || ''
    return (
      message.includes('network') ||
      message.includes('timeout') ||
      message.includes('fetch')
    )
  }

  if (error instanceof Error) {
    const message = error.message?.toLowerCase() || ''
    return (
      message.includes('network') ||
      message.includes('timeout') ||
      message.includes('fetch failed')
    )
  }

  return false
}

/**
 * Extract a user-friendly error message from various error types
 */
export function extractErrorMessage(error: unknown): string {
  if (Array.isArray(error)) {
    const firstMessage = (error[0] as { message?: string } | undefined)?.message
    if (firstMessage) {
      return firstMessage
    }
  }

  if (error instanceof RRNLRequestError) {
    // Handle relay network errors
    const firstError = error.res?.errors?.[0]
    if (firstError?.message) {
      return firstError.message
    }
    // Check if res has a text property (common for network errors)
    if (error.res && typeof error.res === 'object' && 'text' in error.res) {
      return String(error.res.text) || 'Network request failed'
    }
  }

  if (error instanceof Error) {
    return error.message
  }

  if (typeof error === 'string') {
    return error
  }

  return 'An unexpected error occurred'
}

/**
 * Calculate delay for exponential backoff
 */
function getRetryDelay(attemptNumber: number): number {
  // Exponential backoff: 1s, 2s, 4s
  return Math.min(1000 * Math.pow(2, attemptNumber - 1), 4000)
}

/**
 * A wrapper around useMutation that adds standardized error handling and Sentry integration
 *
 * @param mutation - The GraphQL mutation to execute
 * @param config - Configuration options for the mutation. All properties are optional.
 *
 * @example
 * const [createApp] = usePortalMutation(
 *   CREATE_APP_MUTATION,
 *   {
 *     successMessage: 'App created successfully',
 *     errorMessage: 'Failed to create app',
 *     showSuccessToast: true,
 *     showErrorToast: true,
 *     sentryContext: { action: 'create_app' }
 *   }
 * )
 *
 * @example
 * const [updateApp] = usePortalMutation<UpdateAppMutation>(
 *   UPDATE_APP_MUTATION,
 * )
 */
export function usePortalMutation<TMutation extends MutationParameters>(
  mutation: GraphQLTaggedNode,
  config: PortalMutationConfig<TMutation['response']> = {},
): ReturnType<typeof useMutation<TMutation>> {
  const {
    successMessage,
    errorMessage,
    showErrorToast = true,
    mutationName = 'GraphQLMutation',
    enableRetry = true,
  } = config

  // Use a ref to hold the `config` object and avoid re-creating callbacks on every render.
  // This allows us to use the `useCallback` hook below without adding non-primitive config values
  // to its dependency array.
  const configRef = useRef(config)
  configRef.current = config

  const [commit, isInFlight] = useMutation<TMutation>(mutation)

  const wrappedCommit = useCallback(
    (commitConfig: Parameters<typeof commit>[0]) => {
      // Extract non-primitive config values from ref.
      const { sentryContext = {}, onCompleted, onError } = configRef.current

      let retryCount = 0
      let retryToastId: string | number | undefined
      const maxRetries = 3

      const executeCommit = (): ReturnType<typeof commit> => {
        return commit({
          ...commitConfig,
          onCompleted: (response, errors) => {
            // Dismiss any retry toasts on completion
            if (retryToastId !== undefined) {
              toast.dismiss(retryToastId)
              retryToastId = undefined
            }

            // Handle GraphQL errors even when request succeeds
            if (errors && errors.length > 0) {
              const errorMsg =
                errors[0].message || 'Operation completed with errors'
              if (showErrorToast) {
                toast.error(errorMessage || errorMsg)
              }

              captureWithContext(
                new Error(errorMsg),
                {
                  ...sentryContext,
                  component: 'usePortalMutation',
                  operation: mutationName,
                  retryCount,
                },
                'warning',
              )

              // Call custom error handler
              onError?.(errors)
              return
            }

            // Success case
            if (successMessage) {
              toast.success(successMessage)
            }

            // Call custom completion handler
            onCompleted?.(response)

            // Also call the original onCompleted if provided
            commitConfig.onCompleted?.(response, errors)
          },
          onError: (error) => {
            // Check if we should retry
            if (
              enableRetry &&
              retryCount < maxRetries &&
              isRetryableError(error)
            ) {
              retryCount++
              const delay = getRetryDelay(retryCount)

              // Show a toast about retrying (dismiss previous one if exists)
              if (retryToastId !== undefined) {
                toast.dismiss(retryToastId)
              }
              retryToastId = toast.info(
                `Network error. Retrying... (${retryCount}/${maxRetries})`,
              )

              // Retry after delay
              setTimeout(() => {
                executeCommit()
              }, delay)

              return
            }

            // Final error handling (no more retries)
            const errorMsg = extractErrorMessage(error)

            // Dismiss any retry toasts before showing error
            if (retryToastId !== undefined) {
              toast.dismiss(retryToastId)
              retryToastId = undefined
            }

            // Show error toast
            if (showErrorToast) {
              toast.error(errorMessage || errorMsg)
            }

            // Log to Sentry with context
            captureWithContext(error, {
              ...sentryContext,
              component: 'usePortalMutation',
              operation: mutationName,
              retryCount,
            })

            // Call custom error handler
            onError?.(error)

            // Also call the original onError if provided
            commitConfig.onError?.(error)
          },
        })
      }

      return executeCommit()
    },
    [
      commit,
      enableRetry,
      errorMessage,
      mutationName,
      showErrorToast,
      successMessage,
    ],
  )

  return [wrappedCommit, isInFlight]
}
